import onboardingService from "@features/onboarding/services/onboardingService";
import type { User } from "@fitties/shared";
import logger from "@logger";

import { useCurrentUser } from "@shared/queries";
import QUERY_KEYS from "@shared/queryKeys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * React Query mutation hook for updating user profile data.
 *
 * Provides optimistic updates and proper error handling for
 * user profile updates on the profile page.
 *
 * @example
 * ```tsx
 * const { mutate, isPending, error } = useUpdateProfile();
 *
 * const handleUpdate = () => {
 *   mutate({ display_name: "John Doe" });
 * };
 * ```
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: (updates: Partial<User>) => {
      if (!user) {
        throw new Error("User not authenticated");
      }
      return onboardingService.updateOnboardingUser(user.uid, updates);
    },

    onSuccess: async () => {
      if (!user) {
        return;
      }

      // Invalidate and refetch user data
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CURRENT_USER] });
    },

    onError: (error) => {
      logger.error("Failed to update profile:", error);
    },
  });
}
